import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Logo from './Logo';
import {
  LayoutDashboard, Search, Globe, FlaskConical, 
  Bell, FileText, Settings, LogOut, ChevronLeft, ChevronRight,
  Menu, X, User, DollarSign, Target, Zap, BarChart3, Clipboard,
  TrendingUp, Activity, Package, Clock, Shield
} from 'lucide-react';

const navigation = [
  { name: 'Home', href: '/dashboard', icon: LayoutDashboard },
  { divider: true, label: 'Insights' },
  { name: 'CFO Dashboard', href: '/cfo-dashboard', icon: DollarSign },
  { name: 'Pharmacy Dashboard', href: '/pharmacy-dashboard', icon: Activity },
  { name: 'Formulary', href: '/formulary', icon: Clipboard },
  { name: 'Peer Intelligence', href: '/peer-intelligence', icon: Globe },
  { divider: true, label: 'Actions' },
  { name: 'Pipeline', href: '/pipeline', icon: TrendingUp },
  { name: 'Action Center', href: '/action-center', icon: Target },
  { name: 'Interventions', href: '/interventions', icon: Zap },
  { divider: true, label: 'Track' },
  { name: 'Savings Ledger', href: '/savings', icon: BarChart3 },
  { name: 'Timeline', href: '/timeline', icon: Clock },
  { name: 'Reports', href: '/reports', icon: FileText },
  { divider: true, label: 'Explore' },
  { name: 'Drug Search', href: '/search', icon: Search },
  { name: 'Geography', href: '/geography', icon: Globe },
  { name: 'Simulator', href: '/simulator', icon: FlaskConical },
  { divider: true, label: 'Admin' },
  { name: 'Source Health', href: '/source-health', icon: Activity },
  { name: 'Review Queue', href: '/alerts', icon: Bell },
  { name: 'Admin Console', href: '/admin', icon: Shield, adminOnly: true },
  { name: 'Settings', href: '/admin/settings', icon: Settings, adminOnly: true }
];

const roleLabels = {
  admin: 'Administrator',
  platform_admin: 'Platform Admin',
  org_admin: 'Organization Admin',
  hospital_admin: 'Hospital Admin',
  pharmacy_director: 'Pharmacy Director',
  procurement_lead: 'Procurement Lead',
  clinical_reviewer: 'Clinical Reviewer',
  cfo: 'Chief Financial Officer',
  analyst: 'Analyst'
};

export default function Layout({ children }) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const filteredNav = navigation.filter(item => 
    !item.adminOnly || ['admin', 'org_admin', 'platform_admin', 'hospital_admin'].includes(user?.role) || user?.is_platform_admin
  );

  return (
    <div className="min-h-screen bg-[#F9FAFB]" data-testid="main-layout">
      {/* Mobile header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b border-slate-200 z-30 flex items-center justify-between px-4">
        <div className="flex items-center gap-3">
          <button onClick={() => setMobileOpen(true)} className="p-2 hover:bg-slate-100 rounded-lg">
            <Menu className="w-5 h-5 text-slate-600" />
          </button>
          <Logo className="h-10 w-auto" mode="light" />
        </div>
      </div>

      {/* Mobile sidebar overlay */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 bg-slate-900/50 z-40" onClick={() => setMobileOpen(false)} />
      )}

      {/* Sidebar */}
      <aside 
        className={`fixed top-0 left-0 h-full bg-white border-r border-slate-200 z-50 transition-all duration-300 ${
          collapsed ? 'w-20' : 'w-64'
        } ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className={`h-16 flex items-center ${collapsed ? 'justify-center' : 'px-6'} border-b border-slate-200`}>
          <Link to="/dashboard" className="flex items-center gap-3">
            {collapsed ? (
              <Logo compact className="h-12 w-12 text-base" mode="light" />
            ) : (
              <Logo className="h-12 w-auto" mode="light" />
            )}
          </Link>
          <button onClick={() => setMobileOpen(false)} className="lg:hidden absolute right-4 p-2 hover:bg-slate-100 rounded-lg">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Navigation */}
        <nav className={`p-3 space-y-1 overflow-y-auto h-[calc(100vh-200px)] ${collapsed ? 'px-2' : ''}`}>
          {filteredNav.map((item, idx) => {
            if (item.divider) {
              if (collapsed) return null;
              return (
                <div key={idx} className="pt-4 pb-2">
                  <p className="text-xs font-medium text-slate-400 uppercase tracking-wider px-3">
                    {item.label}
                  </p>
                </div>
              );
            }
            
            const isActive = location.pathname === item.href || 
              (item.href !== '/dashboard' && item.href !== '/admin' && location.pathname.startsWith(item.href));
            
            return (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => setMobileOpen(false)}
                className={`sidebar-link ${isActive ? 'active' : ''} ${collapsed ? 'justify-center' : ''} ${
                  item.highlight && !isActive ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100' : ''
                }`}
                title={collapsed ? item.name : undefined}
                data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <item.icon className="w-5 h-5 flex-shrink-0" />
                {!collapsed && <span>{item.name}</span>}
                {item.highlight && !collapsed && !isActive && (
                  <span className="ml-auto text-xs bg-emerald-200 text-emerald-800 px-1.5 py-0.5 rounded">
                    NEW
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* User & Collapse */}
        <div className="absolute bottom-0 left-0 right-0 border-t border-slate-200 bg-white">
          {/* User info */}
          <div className={`p-4 ${collapsed ? 'px-3' : ''}`}>
            {collapsed ? (
              <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center mx-auto">
                <User className="w-5 h-5 text-slate-500" />
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <User className="w-5 h-5 text-slate-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
                  <p className="text-xs text-slate-500 truncate">{roleLabels[user?.role] || user?.role}</p>
                  {user?.organization?.name && <p className="text-[10px] text-slate-400 truncate">{user.organization.name}</p>}
                </div>
              </div>
            )}
          </div>

          {/* Logout */}
          <button
            onClick={handleLogout}
            className={`w-full p-4 flex items-center gap-3 text-slate-600 hover:bg-slate-100 transition-colors ${collapsed ? 'justify-center' : ''}`}
            title={collapsed ? 'Logout' : undefined}
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5" />
            {!collapsed && <span className="text-sm font-medium">Logout</span>}
          </button>

          {/* Collapse toggle */}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="hidden lg:flex w-full p-4 items-center justify-center text-slate-400 hover:bg-slate-100 transition-colors border-t border-slate-100"
          >
            {collapsed ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className={`min-h-screen transition-all duration-300 pt-16 lg:pt-0 ${collapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
        {children}
      </main>
    </div>
  );
}
